import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal, Checkbox, TimePicker, Button, Alert, Spin, Space, Tag, Divider, message,
} from 'antd';
import { ThunderboltOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getClassrooms, generateTermSessions } from '../../api/settings';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Toplu ders üretimi.
 *
 * ÖNCEDEN tek tuştu: dönemdeki TEK saat aralığını Pazar hariç her güne aynı
 * şekilde uyguluyordu. Okul dönemi buna uymuyor — çocuklar gündüz okulda,
 * hafta içi 14:30–20:30, cumartesi 10:30–20:30. Tek aralıkla hafta içine
 * günde 4 fazladan boş slot açılıyordu ve boş slot takvimde doldurulabilir
 * göründüğü için kurum kapalıyken oraya öğrenci kaydedilebiliyordu.
 *
 * ÖNİZLEME ZORUNLU BİR ADIM DEĞİL AMA ÖNE ÇIKARILDI: bu düğme binlerce satır
 * oluşturuyor, yanlış seçim oluşturduktan sonra değil öncesinde fark
 * edilmeli. Önizleme ile gerçek üretim arka uçta AYNI hesabı kullanıyor
 * (SessionPlanner), yani "önizlemede şu yazıyordu, başkası oluştu" olmaz.
 *
 * Seçimler döneme kaydediliyor; form bir dahaki açılışta aynı değerlerle
 * geliyor. Yıl ortasında derslik eklenip tekrar üretim gerekirse "geçen sefer
 * ne seçmiştik" sorusu ortada kalmasın diye.
 */

// Carbon'un dayOfWeek numaralandırması — arka uç da bunu bekliyor.
const GUNLER = [
  { no: 1, ad: 'Pazartesi' },
  { no: 2, ad: 'Salı' },
  { no: 3, ad: 'Çarşamba' },
  { no: 4, ad: 'Perşembe' },
  { no: 5, ad: 'Cuma' },
  { no: 6, ad: 'Cumartesi' },
  { no: 0, ad: 'Pazar' },
];

const VARSAYILAN_HAFTA_ICI = { start: '14:30', end: '20:30' };
const VARSAYILAN_CUMARTESI = { start: '10:30', end: '20:30' };

const GenerateSessionsModal = ({ open, term, onClose, onDone }) => {
  const [derslikler, setDerslikler] = useState([]);
  const [seciliDerslikler, setSeciliDerslikler] = useState([]);
  const [gunAyarlari, setGunAyarlari] = useState({});
  const [onizleme, setOnizleme] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [calisiyor, setCalisiyor] = useState(false);

  const hazirla = useCallback(async () => {
    if (!term) return;
    setYukleniyor(true);
    setOnizleme(null);
    try {
      // exclude_archive: "Geçmiş Dönem" derslikleri geçmiş kayıt arşivi,
      // takvimde de görünmüyorlar — seçim listesinde hiç çıkmasınlar.
      const res = await getClassrooms({
        branch_id: term.branch_id,
        per_page: 100,
        is_active: 1,
        exclude_archive: 1,
      });
      const liste = res.data?.data || [];
      setDerslikler(liste);

      const kayitli = term.session_config;
      if (kayitli?.days) {
        setGunAyarlari(kayitli.days);
        setSeciliDerslikler(kayitli.classroom_ids?.length ? kayitli.classroom_ids : liste.map(d => d.id));
      } else {
        // İlk açılışta okul dönemi varsayılanı öneriliyor; kullanıcı
        // değiştirebilir. Boş form yerine makul bir başlangıç.
        setGunAyarlari({
          1: { ...VARSAYILAN_HAFTA_ICI }, 2: { ...VARSAYILAN_HAFTA_ICI },
          3: { ...VARSAYILAN_HAFTA_ICI }, 4: { ...VARSAYILAN_HAFTA_ICI },
          5: { ...VARSAYILAN_HAFTA_ICI }, 6: { ...VARSAYILAN_CUMARTESI },
        });
        setSeciliDerslikler(liste.map(d => d.id));
      }
    } catch {
      message.error('Derslikler yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }, [term]);

  useEffect(() => { if (open) hazirla(); }, [open, hazirla]);

  const gunDegistir = (gunNo, acik) => {
    setOnizleme(null);
    setGunAyarlari(prev => {
      const yeni = { ...prev };
      if (acik) {
        yeni[gunNo] = gunNo === 6 ? { ...VARSAYILAN_CUMARTESI } : { ...VARSAYILAN_HAFTA_ICI };
      } else {
        delete yeni[gunNo];
      }
      return yeni;
    });
  };

  const saatDegistir = (gunNo, alan, deger) => {
    setOnizleme(null);
    setGunAyarlari(prev => ({
      ...prev,
      [gunNo]: { ...prev[gunNo], [alan]: deger ? deger.format('HH:mm') : null },
    }));
  };

  // Aynı saati beş kez girmek yerine tek tıkla hafta içine uygula.
  const haftaIcineUygula = (gunNo) => {
    const kaynak = gunAyarlari[gunNo];
    if (!kaynak) return;
    setOnizleme(null);
    setGunAyarlari(prev => {
      const yeni = { ...prev };
      [1, 2, 3, 4, 5].forEach(g => { if (yeni[g]) yeni[g] = { ...kaynak }; });
      return yeni;
    });
    message.success('Hafta içi günlerine uygulandı.');
  };

  const gonder = async (previewMi) => {
    const gecerli = Object.entries(gunAyarlari)
      .filter(([, v]) => v?.start && v?.end)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

    if (Object.keys(gecerli).length === 0) {
      message.warning('En az bir gün seçip saat aralığı girin.');
      return;
    }
    if (seciliDerslikler.length === 0) {
      message.warning('En az bir derslik seçin.');
      return;
    }

    setCalisiyor(true);
    try {
      const res = await generateTermSessions(term.id, {
        days: gecerli,
        classroom_ids: seciliDerslikler,
        ...(previewMi ? { preview: true } : {}),
      });

      if (previewMi) {
        setOnizleme(res.data);
      } else {
        message.success(res.data.message);
        onDone?.();
        onClose?.();
      }
    } catch (error) {
      message.error(apiErrorMessage(error, previewMi ? 'Önizleme alınamadı.' : 'Dersler oluşturulamadı.'));
    } finally {
      setCalisiyor(false);
    }
  };

  return (
    <Modal
      title={`Toplu Ders Üret — ${term?.name || ''}`}
      open={open}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="iptal" onClick={onClose}>Vazgeç</Button>,
        <Button key="onizle" icon={<EyeOutlined />} onClick={() => gonder(true)} loading={calisiyor}>
          Önizle
        </Button>,
        <Button
          key="olustur"
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={() => gonder(false)}
          loading={calisiyor}
        >
          Oluştur
        </Button>,
      ]}
    >
      {yukleniyor ? (
        <div className="flex justify-center py-8"><Spin /></div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-3">
            {term?.start_date && term?.end_date && (
              <>Dönem: {dayjs(term.start_date).format('DD.MM.YYYY')} – {dayjs(term.end_date).format('DD.MM.YYYY')}. </>
            )}
            Tatil günleri otomatik atlanır. Ders süresi 60 dakikadır.
          </p>

          <div style={{ fontWeight: 600, marginBottom: 8 }}>Derslikler</div>
          <Checkbox.Group
            value={seciliDerslikler}
            onChange={(v) => { setSeciliDerslikler(v); setOnizleme(null); }}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 4 }}
          >
            {derslikler.map(d => (
              <Checkbox key={d.id} value={d.id}>{d.name}</Checkbox>
            ))}
          </Checkbox.Group>
          {derslikler.length === 0 && (
            <div className="text-xs text-gray-400">Bu şubede uygun derslik yok.</div>
          )}

          <Divider style={{ margin: '16px 0 12px' }} />

          <div style={{ fontWeight: 600, marginBottom: 8 }}>Günler ve saatler</div>
          {GUNLER.map(({ no, ad }) => {
            const acik = !!gunAyarlari[no];
            return (
              <div key={no} className="flex items-center gap-2 mb-2">
                <Checkbox
                  checked={acik}
                  onChange={(e) => gunDegistir(no, e.target.checked)}
                  style={{ width: 120 }}
                >
                  {ad}
                </Checkbox>
                {acik ? (
                  <Space size={4}>
                    <TimePicker
                      format="HH:mm"
                      minuteStep={5}
                      size="small"
                      allowClear={false}
                      value={gunAyarlari[no]?.start ? dayjs(gunAyarlari[no].start, 'HH:mm') : null}
                      onChange={(v) => saatDegistir(no, 'start', v)}
                    />
                    <span className="text-gray-400">–</span>
                    <TimePicker
                      format="HH:mm"
                      minuteStep={5}
                      size="small"
                      allowClear={false}
                      value={gunAyarlari[no]?.end ? dayjs(gunAyarlari[no].end, 'HH:mm') : null}
                      onChange={(v) => saatDegistir(no, 'end', v)}
                    />
                    {[1, 2, 3, 4, 5].includes(no) && (
                      <Button size="small" type="link" onClick={() => haftaIcineUygula(no)}>
                        hafta içine uygula
                      </Button>
                    )}
                  </Space>
                ) : (
                  <span className="text-xs text-gray-400">kapalı</span>
                )}
              </div>
            );
          })}

          {onizleme && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message={`${onizleme.yeni} yeni ders oluşturulacak`}
              description={
                <div className="text-xs">
                  {onizleme.atlanacak > 0 && (
                    <div>{onizleme.atlanacak} ders zaten var, atlanacak (mevcut dersler silinmez).</div>
                  )}
                  <div>
                    {onizleme.derslik_sayisi} derslik · {onizleme.gun_sayisi} gün
                    {onizleme.tatil_sayisi > 0 && ` · ${onizleme.tatil_sayisi} tatil günü tanımlı`}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    {onizleme.gun_ozeti?.map(g => (
                      <Tag key={g.gun}>{g.gun}: {g.adet}</Tag>
                    ))}
                  </div>
                </div>
              }
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default GenerateSessionsModal;
